import { inject } from '@angular/core';
import type { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { RoleName } from './api/graphql/gqty';
import { Main } from './pages/main/main';
import { AuthService, provideUser } from './services/auth-service';

const isLoggedIn = async () => {
  const auth = inject(AuthService);

  try {
    const user = await auth.getMe();
    return !!user;
  } catch {
    window.location.href = environment.loginFlowUrl;
    return false;
  }
};

const adminGuard = async () => {
  const auth = inject(AuthService);
  const user = await auth.getMe();
  if (user.role !== RoleName.ADMIN) {
    return false;
  }
  return true;
};

export const routes: Routes = [
  {
    path: 'create-account',
    loadComponent: () =>
      import('./pages/create-account-page/create-account-page').then(
        (m) => m.default,
      ),
  },
  {
    path: 'confirm-email',
    loadComponent: () =>
      import('./pages/confirm-email-page/confirm-email-page').then(
        (m) => m.default,
      ),
  },
  {
    path: 'not-found',
    loadComponent: () =>
      import('./pages/not-found-page/not-found-page').then((m) => m.default),
  },
  {
    path: '',
    canMatch: [isLoggedIn],
    providers: [provideUser()],
    component: Main,
    children: [
      {
        path: 'dashboard',
        loadComponent: async () => {
          return import('./pages/dashboard-page/dashboard-page').then(
            (m) => m.default,
          );
        },
      },
      {
        path: 'map',
        loadComponent: () =>
          import('./pages/map-page/map-page').then((m) => m.default),
      },
      {
        path: 'projects',
        loadComponent: () =>
          import('./pages/projects-page/projects-page').then((m) => m.default),
      },
      {
        path: 'organizations',
        canMatch: [adminGuard],
        loadComponent: () =>
          import('./pages/organizations-page/organizations-page').then(
            (m) => m.default,
          ),
        children: [
          {
            path: 'side/:organizationId',
            loadComponent: () =>
              import(
                './pages/organization-side-detail/organization-side-detail'
              ).then((m) => m.default),
          },
        ],
      },
      {
        path: 'organizations/:organizationId',
        canMatch: [adminGuard],
        loadComponent: () =>
          import(
            './pages/organization-detail-page/organization-detail-page'
          ).then((m) => m.default),
      },
      {
        path: 'users',
        canMatch: [adminGuard],
        loadComponent: () =>
          import('./pages/users-page/users-page').then((m) => m.default),
      },
      {
        path: 'settings',
        canMatch: [adminGuard],
        loadComponent: () =>
          import('./pages/settings-page/settings-page').then((m) => m.default),
      },
      {
        path: 'projects/:projectId',
        loadComponent: () =>
          import('./pages/project-detail-page/project-detail-page').then(
            (m) => m.default,
          ),
        children: [
          {
            path: 'assets/side/:assetId',
            loadComponent: () =>
              import('./pages/asset-side-detail/asset-side-detail').then(
                (m) => m.default,
              ),
          },
        ],
      },
      {
        path: 'projects/:projectId/surveys/:surveyId',
        loadComponent: () =>
          import(
            './pages/project-survey-detail-page/project-survey-detail-page'
          ).then((m) => m.default),
      },
      {
        path: 'projects/:projectId/interventions/:interventionId',
        loadComponent: () =>
          import(
            './pages/intervention-detail-page/intervention-detail-page'
          ).then((m) => m.default),
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'map',
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'map',
  },
];
